import React from 'react';
import styles from './tabs.module.css';
import { useViewModel } from './useViewModel.js';

/**
 * Accessible Tabs component.
 * @param {Object} props - Component props.
 * @param {Array<{ id: string, label: string }>} props.tabs - Tab definitions.
 * @param {boolean} [props.fullWidth=false] - Whether to make the tabs full width.
 * @param {number} [props.activeIndex=0] - Controlled active tab index.
 * @param {(index:number)=>void} [props.onChange] - Change handler.
 * @param {string} [props.className=''] - Optional class names.
 * @returns {import('react').JSX.Element}
 */
export function Tabs({ tabs = [], activeIndex = 0, onChange, className = '', fullWidth = false }) {
  const { currentIndex, handleClick, handleKeyDown, getTabId, getPanelId } = useViewModel({
    tabs,
    activeIndex,
    onChange
  });

  return (
    <div className={`${styles.tabs} ${fullWidth ? styles['tabs--full-width'] : ''} ${className}`}>
      <div
        className={`${styles.tabs__list} ${fullWidth ? styles['tabs__list--full-width'] : ''}`}
        role="tablist"
        aria-label="Insert faces"
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            id={getTabId(index)}
            className={`${styles.tabs__tab} ${index === currentIndex ? styles['tabs__tab--active'] : ''} ${fullWidth ? styles['tabs__tab--full-width'] : ''}`}
            role="tab"
            type="button"
            aria-selected={index === currentIndex}
            aria-controls={getPanelId(index)}
            tabIndex={index === currentIndex ? 0 : -1}
            onClick={() => handleClick(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default Tabs;
